import React from 'react';
import { McTag } from '@maersk-global/mds-react-wrapper/components-core/mc-tag';
import { humanize } from './DeployToCdt';
import { COLORS, SPACING, BORDER_RADIUS, FONT_FAMILY } from '../../constants/theme';

// 'config.structureModel' -> 'Structure Model', 'version' -> 'Version'
export const fieldLabel = (field) => humanize(field.replace(/^config\./, ''));

const asText = (value) => {
  if (value === undefined || value === null || value === '') return '';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const isShort = (text) => text.length <= 60 && !text.includes('\n');

const sectionLabel = {
  color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: SPACING.xs
};

const Empty = () => <em style={{ color: COLORS.textSecondary }}>(empty)</em>;

const ValueBlock = ({ label, text, tone }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary, marginBottom: 2 }}>{label}</div>
    <pre className="mds-text--x-small-normal" style={{
      margin: 0, padding: SPACING.sm,
      backgroundColor: tone === 'before' ? COLORS.errorBg : COLORS.successBg,
      border: `1px solid ${tone === 'before' ? COLORS.errorBorder : COLORS.borderLight}`,
      borderRadius: BORDER_RADIUS.sm,
      fontFamily: FONT_FAMILY,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      maxHeight: 220, overflowY: 'auto', color: COLORS.text
    }}>
      {text || <Empty />}
    </pre>
  </div>
);

const ChangeRow = ({ change }) => {
  const before = asText(change.before);
  const after = asText(change.after);
  return (
    <div style={{ padding: `${SPACING.sm}px 0`, borderTop: `1px solid ${COLORS.borderLight}` }}>
      <div className="mds-text--small-bold" style={{ color: COLORS.text, marginBottom: SPACING.xs }}>
        {fieldLabel(change.field)}
      </div>
      {isShort(before) && isShort(after) ? (
        <div className="mds-text--small-normal" style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ color: COLORS.error, textDecoration: 'line-through' }}>{before || <Empty />}</span>
          <span style={{ color: COLORS.textSecondary }}>→</span>
          <span style={{ color: COLORS.success }}>{after || <Empty />}</span>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: SPACING.sm }}>
          <ValueBlock label="Before" text={before} tone="before" />
          <ValueBlock label="After" text={after} tone="after" />
        </div>
      )}
    </div>
  );
};

// One line per capability in the History drawer: "NER: System Prompt, Structure Model".
export const DeploySummary = ({ deployment, deployedBy }) => (
  <div className="mds-text--x-small-normal" style={{ color: COLORS.text }}>
    {deployedBy || 'Someone'} deployed to <strong>{deployment.workflow_name || 'a CDT workflow'}</strong>
    {(deployment.contexts || []).map(c => (
      <div key={c.id || c.capability} style={{ color: COLORS.textSecondary }}>
        <strong style={{ color: COLORS.text }}>{c.capability}</strong>
        {': '}
        {c.fields ? (c.fields.length ? c.fields.map(fieldLabel).join(', ') : 'no changes') : c.name}
      </div>
    ))}
  </div>
);

// Full before/after per capability, for the version View modal.
export const DeployChanges = ({ deployment }) => {
  const changes = deployment?.changes || [];
  if (!changes.length) return null;
  const groups = [];
  changes.forEach(ch => {
    const key = ch.context_id || ch.capability;
    let group = groups.find(g => g.key === key);
    if (!group) {
      group = { key, capability: ch.capability, name: ch.context_name, changes: [] };
      groups.push(group);
    }
    group.changes.push(ch);
  });
  return (
    <div>
      <div className="mds-text--x-small-bold" style={sectionLabel}>Changes deployed to {deployment.workflow_name || 'CDT'}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
        {groups.map(g => (
          <div key={g.key} style={{ border: `1px solid ${COLORS.border}`, borderRadius: BORDER_RADIUS.md, padding: `${SPACING.sm}px ${SPACING.md}px` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.xs }}>
              <McTag appearance="neutral-inverse" fit="small">{g.capability}</McTag>
              <span className="mds-text--small-normal" style={{ color: COLORS.textSecondary }}>{g.name}</span>
            </div>
            {g.changes.map(ch => <ChangeRow key={ch.field} change={ch} />)}
          </div>
        ))}
      </div>
    </div>
  );
};
